/* eslint-disable no-underscore-dangle */
import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  addDays,
  SelectOption,
  today,
  toIso8601String,
} from '@bcl/design-system';
import {
  AddProductDialogData,
  AddProductResult,
  AddProductService,
} from './consumables-dialog.service';
import { DocumentsListApiService } from 'src/app/pages/visit-day-details/components/visit-documents/visit-documents.service';
import { SnackbarService } from '@bcl/design-system';

@Component({
  selector: 'app-consumables-dialog',
  templateUrl: './consumables-dialog.component.html',
  styleUrls: ['./consumables-dialog.component.scss'],
})
export class ConsumablesDialogComponent {
  public actionType: string;
  public form: FormGroup;
  public productOptions: SelectOption[] = [];
  public supplierOptions: SelectOption[] = [];
  public selectedProduct: any = null;
  public defaultCar: number | null = null;
  public downloadingMsds = false;
  public monitors: Array<{ id: number; name: string; moduleId?: string }> = [];
  public selectedMonitors: Set<number> = new Set();
  public currentRangeStart = 0;
  public rangeButtons: Array<{ start: number; end: number; label: string }> =
    [];
  public visibleMonitors: Array<{
    id: number;
    name: string;
    displayName: string;
    checked: boolean;
  }> = [];
  public executeMode: 'all' | 'selected' | null = null;
  private allProducts: any[] = [];
  private _minExpirationDate: Date = addDays(today(), 1);

  constructor(
    fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public readonly data: AddProductDialogData,
    private readonly dialogRef: MatDialogRef<
      ConsumablesDialogComponent,
      AddProductResult
    >,
    private readonly addProductService: AddProductService,
    private readonly changeDet: ChangeDetectorRef,
    private readonly documentsApiService: DocumentsListApiService,
    private readonly snackbarService: SnackbarService
  ) {
    this.actionType = data.operationType;
    this.defaultCar = data.defaultCar;

    this.form = fb.group({
      warehouseId: [null, Validators.required],
      productId: [null, Validators.required],
      supplierId: null,
      amount: [null, Validators.required],
      executeAll: [false],
      executeSelected: [false],
      allowToDisappear: [false],
    });

    this.loadMonitors();

    this.form.get('executeAll')?.valueChanges.subscribe((value) => {
      if (value) {
        this.form.get('executeSelected')?.setValue(false, { emitEvent: false });
        this.executeMode = 'all';
        this.selectedMonitors.clear();
      } else if (!this.form.get('executeSelected')?.value) {
        this.executeMode = null;
      }
    });

    this.form.get('executeSelected')?.valueChanges.subscribe((value) => {
      if (value) {
        this.form.get('executeAll')?.setValue(false, { emitEvent: false });
        this.executeMode = 'selected';
      } else if (!this.form.get('executeAll')?.value) {
        this.executeMode = null;
      }
    });

    if (this.actionType !== 'storeInWarehouse') {
      this.form.get('warehouseId')?.valueChanges.subscribe((warehouseId) => {
        this.form.get('productId')?.setValue(null);
        this.productOptions = [];
        this.getProductsForWarehouse(warehouseId);
      });

      this.form.get('productId')?.valueChanges.subscribe((productId) => {
        this.selectedProduct = this.allProducts.find(
          (product) => product.rowId === productId
        );
        this.form.get('supplierId')?.setValue(this.selectedProduct?.supplierId);
        this.form.get('amount')?.setValue(1);
        this.form.get('allowToDisappear')?.setValue(false);
      });

      // Pre-select default warehouse for add, replace, and storeInWarehouse actions
      // Only if user has a default warehouse
      if (
        this.defaultCar &&
        (this.actionType === 'add' ||
          this.actionType === 'storeInWarehouse' ||
          this.actionType === 'replace')
      ) {
        // Use setValue to ensure valueChanges subscription triggers
        this.form.get('warehouseId')?.setValue(this.defaultCar.toString());
      }
    } else {
      this.form.controls.productId.setValidators(null);
      this.form.controls.amount.setValidators(null);
      if (this.defaultCar) {
        this.form.get('warehouseId')?.setValue(this.defaultCar.toString());
      }
      this.form.markAsTouched();
    }
  }
  public get canSubmit() {
    if (
      this.form.get('executeSelected')?.value &&
      this.selectedMonitors.size === 0
    ) {
      return false;
    }

    if (this.actionType === 'delete') {
      return true;
    }

    if (this.actionType === 'storeInWarehouse') {
      return this.form.get('warehouseId')?.valid ?? false;
    }

    return this.form.valid;
  }

  public get hasMsdsReference() {
    return (
      this.selectedProduct?.referenceID != null &&
      this.selectedProduct?.referenceID !== undefined
    );
  }

  public get canAllowToDisappear() {
    return this.selectedProduct?.allowToDisappear === true;
  }

  public get minExpirationDate() {
    return toIso8601String(this._minExpirationDate);
  }

  public get isExecuteAll(): boolean {
    return this.form.get('executeAll')?.value === true;
  }

  public get isExecuteSelected(): boolean {
    return this.form.get('executeSelected')?.value === true;
  }

  public get shouldShowExecuteOptions(): boolean {
    if (this.monitors.length === 0) {
      return false;
    }

    if (this.monitors.length === 1 && this.data.newMonitorId) {
      return this.monitors[0].id !== this.data.newMonitorId;
    }

    return true;
  }

  public getMaxAmount(): number {
    if (!this.selectedProduct?.amount) {
      return 0;
    }

    if (this.form.get('executeAll')?.value) {
      return this.selectedProduct.amount / this.data.sameMonitorsCount;
    }

    if (
      this.form.get('executeSelected')?.value &&
      this.selectedMonitors.size > 0
    ) {
      return this.selectedProduct.amount / this.selectedMonitors.size;
    }

    return this.selectedProduct.amount;
  }

  public submit() {
    const { expirationDate, ...rest } = this.form.getRawValue();
    this.dialogRef.close({
      ...rest,
      productId: this.selectedProduct?.productId,
      expirationDate: expirationDate ? new Date(expirationDate) : null,
      unitId: this.selectedProduct?.unit,
      action: this.actionType,
      monitorProductId: this.data.product?.id,
      executeAll: this.executeMode === 'all',
      executeSelected: this.executeMode === 'selected',
      allowToDisappear: this.form.get('allowToDisappear')?.value || false,
      selectedMonitors:
        this.executeMode === 'selected'
          ? Array.from(this.selectedMonitors)
          : [],
    });
  }

  public loadMonitors() {
    const monitorId = this.data.monitorId || 0;
    const visitId = this.data.visitId || 0;

    if (!monitorId || !visitId) {
      return;
    }

    this.addProductService
      .getMonitorsForSelection(monitorId, visitId)
      .subscribe({
        next: (response: any[]) => {
          this.monitors =
            response?.map((monitor: any) => ({
              id: monitor.id,
              name: monitor.name || `Monitor ${monitor.id}`,
              moduleId: monitor.module,
            })) || [];

          if (!this.shouldShowExecuteOptions) {
            this.form.get('executeAll')?.setValue(false, { emitEvent: false });
            this.form
              .get('executeSelected')
              ?.setValue(false, { emitEvent: false });
            this.executeMode = null;
            this.selectedMonitors.clear();
          }

          this.generateRangeButtons();
          this.selectRange(0);
        },
        error: (error) => {
          console.error('Error loading monitors:', error);
          this.monitors = [];
          this.form.get('executeAll')?.setValue(false, { emitEvent: false });
          this.form
            .get('executeSelected')
            ?.setValue(false, { emitEvent: false });
          this.executeMode = null;
          this.selectedMonitors.clear();
        },
      });
  }

  public generateRangeButtons() {
    this.rangeButtons = [];
    const totalMonitors = this.monitors.length;
    const rangeSize = 10;

    for (let i = 0; i < totalMonitors; i += rangeSize) {
      const start = i + 1;
      const end = Math.min(i + rangeSize, totalMonitors);
      this.rangeButtons.push({
        start: i,
        end,
        label: `${start}-${end}`,
      });
    }
  }

  public selectRange(startIndex: number) {
    this.currentRangeStart = startIndex;
    const endIndex = Math.min(startIndex + 10, this.monitors.length);
    this.visibleMonitors = this.monitors
      .slice(startIndex, endIndex)
      .map((monitor) => ({
        ...monitor,
        displayName: this.getMonitorDisplayName(monitor),
        checked: this.selectedMonitors.has(monitor.id),
      }));
  }

  public toggleMonitor(monitorId: number, checked: boolean) {
    if (checked) {
      this.selectedMonitors.add(monitorId);
    } else {
      this.selectedMonitors.delete(monitorId);
    }
    const monitor = this.visibleMonitors.find((m) => m.id === monitorId);
    if (monitor) {
      monitor.checked = checked;
    }
    this.changeDet.detectChanges();
  }

  public downloadMsds() {
    if (!this.selectedProduct?.referenceID) {
      return;
    }

    this.downloadingMsds = true;

    this.documentsApiService
      .downloadFile(this.selectedProduct.referenceID)
      .subscribe({
        next: (blob) => {
          const fileName =
            this.selectedProduct.documentName ||
            `MSDS_${this.selectedProduct.name}`;
          this.documentsApiService.downloadFileToDisk(blob, fileName);

          this.snackbarService.open(
            'green',
            'DOCUMENTS_LIST.MESSAGES.DOWNLOAD_DOCUMENT_SUCCESS'
          );

          this.downloadingMsds = false;
        },
        error: () => {
          this.snackbarService.open(
            'red',
            'DOCUMENTS_LIST.MESSAGES.DOWNLOAD_DOCUMENT_ERROR'
          );

          this.downloadingMsds = false;
        },
      });
  }

  public getProductsForWarehouse(warehouseId: number | null) {
    if (warehouseId) {
      this.addProductService
        .getProductsForWarehouse(warehouseId, this.data.moduleId)
        .subscribe((products: any) => {
          this.allProducts = products?.elements ?? [];
          let counter = 0;
          this.allProducts.forEach((product) => {
            product.rowId = counter++;
          });
          this.productOptions = products?.elements.map((product: any) => ({
            value: product.rowId,
            label:
              product.name +
              (product.supplier ? ' | ' + product.supplier : '') +
              ' | ' +
              product.amount +
              ' ' +
              product.unit,
          }));

          // Auto-select matching product for replace action
          // This needs to happen after the products are loaded and the UI is updated
          if (this.actionType === 'replace' && this.data.product) {
            // Use setTimeout to ensure the UI is updated before auto-selection
            setTimeout(() => {
              this.autoSelectMatchingProduct();
            }, 100);
          }
        });
    } else {
      this.productOptions = [];
      this.allProducts = [];
    }
  }

  public getSuppliersForProduct(productId: number | null) {
    if (productId) {
      this.addProductService
        .getSuppliersForProduct(productId)
        .subscribe((suppliers: any) => {
          this.supplierOptions = suppliers?.elements.map((supplier: any) => ({
            value: supplier.supplierId,
            label: supplier.supplierName,
          }));
        });
    }
  }

  private autoSelectMatchingProduct() {
    if (!this.data.product || !this.allProducts.length) {
      return;
    }

    // Find the product with the same name as the one being replaced
    const matchingProduct = this.allProducts.find(
      (product) => product.name === this.data.product?.name
    );

    if (matchingProduct) {
      // Auto-select the matching product
      this.form.get('productId')?.setValue(matchingProduct.rowId);

      // The amount will be automatically set to 1 by the productId valueChanges subscription
      // But we can ensure it's set here as well
      setTimeout(() => {
        this.form.get('amount')?.setValue(1);
        this.changeDet.detectChanges();
      }, 0);
    }
  }

  private getMonitorDisplayName(monitor: {
    id: number;
    name: string;
    moduleId?: string;
  }): string {
    if (!monitor.moduleId) {
      return monitor.name;
    }

    const modules = (this.data as any).modules;
    const moduleOption = modules?.find(
      (m: SelectOption) => m.value === monitor.moduleId
    );

    const moduleName = moduleOption?.label || monitor.moduleId;
    return `${monitor.name} (${moduleName})`;
  }
}
